import { evolve, pipe } from 'remeda'
import { z } from 'zod'
import { checkForInaccurateSetId } from '~/features/models/models-list/validators/common'
import { appendWarning } from '~/features/models/models-list/validators/utils'
import { ModelV2 } from '~/supabase/types'
import { materials, productTypes, modelStatuses, productTypeKeyMap } from '~/utils/constants'
import { resolveJSONParse } from '../../../../../shared/utils'

export const modelSchemaBase = z.object({
  stock_code: z.string(),
  supplier: z.number(),
  supplier_code: z.string(),
  price: z.coerce.number(),
  garnet_ct: z.coerce.number(),
  diamond_ct: z.coerce.number(),
  material: z.enum(materials),
  product_type: z.enum(productTypes),
  weight: z.coerce.number(),
  set_id: z.coerce.number(),
  status: z.enum(modelStatuses),
  status_v2: z.enum(modelStatuses),
  shopify_product_id: z.string().nullable().optional().default(null),
  image_urls: z.array(z.string()).nullable().optional().default(null),
})

const modelSchema: z.ZodType<
  Omit<ModelV2, 'id' | 'created_at' | 'updated_at' | 'image_urls' | 'shopify_product_id'> & {
    warning?: string[]
    image_urls?: string[] | null
    shopify_product_id?: string | null
  }
> = modelSchemaBase
  .omit({ image_urls: true })
  .extend({
    image_urls: z
      .array(z.string())
      .nullable()
      .superRefine((data, ctx) => {
        if (!data) return
        const parsed = z.preprocess(resolveJSONParse, z.array(z.string())).safeParse(data)
        if (parsed.error) {
          ctx.addIssue({
            path: ['image_urls'],
            code: 'custom',
            message: 'Failed to parse image array',
          })
        }
      })
      .optional()
      .default([]),
    warning: z.array(z.string()).optional().default([]),
  })
  .superRefine((data, ctx) => {
    const { warning } = pipe(
      data,
      checkForCarats,
      checkForMaterial,
      checkForType,
      checkSize,
      checkForStatus
    )
    if (warning && warning.length > 0) {
      warning.forEach((message) => {
        ctx.addIssue({ code: 'custom', message })
      })
    }

    const message = checkForInaccurateSetId(data.stock_code)
    if (message) {
      ctx.addIssue({ code: 'custom', path: ['size'], message })
    }
  })

export const modelArraySchema = z.array(modelSchema)

export const modelSample: Omit<ModelSchemaT, 'warning'>[] = [
  {
    stock_code: 'GORI00100',
    supplier: 1,
    supplier_code: 'GM-00053',
    price: 52000,
    garnet_ct: 0.4,
    diamond_ct: 0.01,
    material: 'rose_diamond',
    product_type: 'ring',
    weight: 1,
    set_id: 1,
    status: 'incomplete',
    image_urls: null,
    status_v2: 'incomplete',
    shopify_product_id: null,
  },
]

export type ModelSchemaT = z.TypeOf<typeof modelSchema>
export type ModelArraySchemaT = z.TypeOf<typeof modelArraySchema>

export const checkForCarats = (v: ModelSchemaT) => {
  const isDiamond = v.stock_code.toLowerCase().startsWith('di')

  return isDiamond && !(v.diamond_ct && v.diamond_ct > 0)
    ? evolve(v, {
        warning: appendWarning(`Diamond ct value or stock_code is wrong for ${v.stock_code}`),
      })
    : v
}

export const checkForMaterial = (v: ModelSchemaT) => {
  const isDiamond = v.stock_code.toLowerCase().startsWith('di')
  if (isDiamond && !v.material.includes('diamond'))
    return evolve(v, {
      warning: appendWarning(`Material (diamond) or stock_code is wrong ${v.stock_code}`),
    })

  const isGold = v.stock_code.toLowerCase().startsWith('go')

  if (isGold && !v.material.includes('gold'))
    return evolve(v, {
      warning: appendWarning(`Material (gold) or stock_code is wrong ${v.stock_code}`),
    })

  const isSilver = v.stock_code.toLowerCase().startsWith('si')

  if (isSilver && !v.material.includes('silver'))
    return evolve(v, {
      warning: appendWarning(`Material (silver) or stock_code is wrong ${v.stock_code}`),
    })

  const isMoldavite = v.stock_code.toLowerCase().startsWith('mo')

  if (isMoldavite && !v.material.includes('moldavite'))
    return evolve(v, {
      warning: appendWarning(`Material (moldavite) or stock_code is wrong ${v.stock_code}`),
    })

  const isYellow = v.stock_code.toLowerCase().endsWith('y')
  if (isYellow && !v.material.includes('yellow'))
    return evolve(v, {
      warning: appendWarning(`Material must have "yellow" for the stock code: ${v.stock_code}`),
    })

  const isWhite = v.stock_code.toLowerCase().endsWith('w')
  if (isWhite && !v.material.includes('white'))
    return evolve(v, {
      warning: appendWarning(`Material must have "white" for the stock code: ${v.stock_code}`),
    })

  // we assume its rose gold if the ending char is a number. Maybe this is unreliable ?
  const { success: endsWithNumber } = z.coerce
    .number()
    .safeParse(v.stock_code.toLowerCase().charAt(v.stock_code.length - 1))

  const prefices = ['di'].concat(Object.values(productTypeKeyMap))
  const isRose = endsWithNumber && prefices.some((p) => v.stock_code.toLowerCase().startsWith(p))

  if (isRose && !v.material.includes('rose') && !v.material.includes('silver'))
    return evolve(v, {
      warning: appendWarning(`Material must have "rose" for the stock code: ${v.stock_code}`),
    })

  return v
}

export const checkForType = (v: ModelSchemaT) => {
  const typeKey = productTypeKeyMap[v.product_type]
  const typeCode = v.stock_code.toLowerCase().substring(2, 4)

  if (typeKey !== typeCode) {
    return evolve(v, {
      warning: appendWarning(`Stock code and product type does not match! ${v.stock_code}`),
    })
  }

  return v
}

export const checkSize = (v: ModelSchemaT) => {
  const size = v.stock_code.toLowerCase().substring(7, 9)

  if (size !== '00') {
    return evolve(v, {
      warning: appendWarning(`Size must be 00! - ${v.stock_code}`),
    })
  }

  return v
}

export const checkForStatus = (v: ModelSchemaT) => {
  if (v.status !== 'incomplete') {
    return evolve(v, {
      warning: appendWarning(`Status must be incomplete! - ${v.stock_code}`),
    })
  }

  return v
}
